import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import CrmSettingService from "@/services/api/crmSetting";
import { handleResponse } from "@/services/axios";

const { ENDPOINTS } = CrmSettingService;

export const updateCrmSetting = createAsyncThunk(
  "crmSetting/updateCrmSetting",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await CrmSettingService.updateCrmSetting(payload);
      const result = handleResponse(res, rejectWithValue);
      return result;
    } catch (err: any) {
      return rejectWithValue(err?.Message);
    }
  },
);

export const fetchCrmSetting = createAsyncThunk(
  "crmSetting/fetchCrmSetting",
  async (_, { rejectWithValue }) => {
    try {
      const response = await CrmSettingService.fetchCrmSetting();
      return handleResponse(response, rejectWithValue);
    } catch (error: any) {
      return rejectWithValue(error?.Message ?? "Failed to fetch crm setting");
    }
  },
);

export const fetchSaleTargetFields = createAsyncThunk(
  "crmSetting/fetchSaleTargetFields",
  async (_, { rejectWithValue }) => {
    try {
      const response = await CrmSettingService.fetchSaleTargetFields();
      return handleResponse(response, rejectWithValue);
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to fetch sale target fields",
      );
    }
  },
);

export const fetchSalesTargetUnit = createAsyncThunk(
  "crmSetting/fetchSalesTargetUnit",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await CrmSettingService.fetchSalesTargetUnit(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to fetch Sales target unit data",
      );
    }
  },
);

const createFetchThunk = (name: string, endpoint: string) =>
  createAsyncThunk(
    `crmSetting/${name}`,
    async (silent: boolean = false, { rejectWithValue }) => {
      try {
        const response = await CrmSettingService.fetchList(endpoint);
        const result = handleResponse(response, rejectWithValue);
        return { result, silent };
      } catch (error: any) {
        return rejectWithValue(error?.Message || `Failed to fetch ${name}`);
      }
    },
  );

const createDeleteThunk = (name: string, endpoint: string) =>
  createAsyncThunk(
    `crmSetting/delete${name}`,
    async (id: number, { rejectWithValue }) => {
      try {
        const response = await CrmSettingService.deleteItem(endpoint, id);
        const result = handleResponse(response, rejectWithValue);
        return { id, result };
      } catch (err: any) {
        return rejectWithValue(err?.Message || `Failed to delete ${name}`);
      }
    },
  );

const createAddThunk = (
  name: string,
  endpoint: string,
  fetchThunk: any,
  stringify = false,
) =>
  createAsyncThunk(
    `crmSetting/add${name}`,
    async (payload: any, { rejectWithValue, dispatch }) => {
      try {
        const res = await CrmSettingService.addItem(
          endpoint,
          payload,
          stringify,
        );
        dispatch(fetchThunk(true));
        return handleResponse(res, rejectWithValue);
      } catch (err: any) {
        return rejectWithValue(err?.Message);
      }
    },
  );

export const fetchCustomerStage = createFetchThunk(
  "fetchCustomerStage",
  ENDPOINTS.customerStage,
);

export const deleteCustomerStage = createDeleteThunk(
  "CustomerStage",
  ENDPOINTS.customerStage,
);

export const addCustomerStage = createAddThunk(
  "CustomerStage",
  ENDPOINTS.customerStage,
  fetchCustomerStage,
  true,
);

export const fetchCustomerSource = createFetchThunk(
  "fetchCustomerSource",
  ENDPOINTS.customerSource,
);

export const deleteCustomerSource = createDeleteThunk(
  "CustomerSource",
  ENDPOINTS.customerSource,
);

export const addCustomerSource = createAddThunk(
  "CustomerSource",
  ENDPOINTS.customerSource,
  fetchCustomerSource,
);

export const fetchVisitPurpose = createFetchThunk(
  "fetchVisitPurpose",
  ENDPOINTS.visitPurpose,
);

export const deleteVisitPurpose = createDeleteThunk(
  "VisitPurpose",
  ENDPOINTS.visitPurpose,
);

export const addVisitPurpose = createAddThunk(
  "VisitPurpose",
  ENDPOINTS.visitPurpose,
  fetchVisitPurpose,
  true,
);

export const fetchCrmLeadType = createFetchThunk(
  "CrmLeadType",
  ENDPOINTS.crmLead,
);

export const deleteCrmLeadType = createDeleteThunk(
  "CrmLeadType",
  ENDPOINTS.crmLead,
);

export const addOrUpdateCrmLeadType = createAddThunk(
  "CrmLeadType",
  ENDPOINTS.crmLead,
  fetchCrmLeadType,
  true,
);

export const fetchCustomerAuditTeamMembers = createFetchThunk(
  "fetchCustomerAuditTeamMembers",
  ENDPOINTS.customerAuditTeamMembers,
);

export const setCustomerAuditTeamMembers = createAsyncThunk(
  "crmSetting/setCustomerAuditTeamMembers",
  async (payload: { SelectedUserIds: number[] }, { rejectWithValue, dispatch }) => {
    try {
      const res = await CrmSettingService.setCustomerAuditTeamMembers(payload);
      const result = handleResponse(res, rejectWithValue);
      dispatch(fetchCustomerAuditTeamMembers(true));
      return result;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message || "Failed to set customer audit team members");
    }
  },
);

export const fetchRoleCustomerViewLimits = createFetchThunk(
  "fetchRoleCustomerViewLimits",
  ENDPOINTS.roleCustomerViewLimits,
);

export const setRoleCustomerViewLimits = createAsyncThunk(
  "crmSetting/setRoleCustomerViewLimits",
  async (
    payload: number[],
    { rejectWithValue, dispatch }
  ) => {
    try {
      const res = await CrmSettingService.setRoleCustomerViewLimits(payload);
      const result = handleResponse(res, rejectWithValue);
      dispatch(fetchRoleCustomerViewLimits(true));
      return result;
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to set role customer view limits");
    }
  },
);

interface CrmSettingState {
  customerStages: {
    loading: boolean;
    data: any[];
  };
  customerSource: {
    loading: boolean;
    data: any[];
  };
  visitPurpose: {
    loading: boolean;
    data: any[];
  };
  crmSetting: any;
  saleTargetFields: any[] | null;

  crmLead: {
    loading: boolean;
    data: any[];
  };

  customerAuditTeamMembers: {
    loading: boolean;
    data: any[];
  };

  roleCustomerViewLimits: {
    loading: boolean;
    data: any[];
  };
}

const initialState: CrmSettingState = {
  customerStages: { loading: false, data: [] },
  customerSource: { loading: false, data: [] },
  visitPurpose: { loading: false, data: [] },
  crmLead: { loading: false, data: [] },
  customerAuditTeamMembers: { loading: false, data: [] },
  roleCustomerViewLimits: { loading: false, data: [] },
  crmSetting: null,
  saleTargetFields: null,
};

const createListReducers = (builder: any, thunk: any, key: string) => {
  builder.addCase(thunk.pending, (state: any, action: any) => {
    if (!action.meta.arg) {
      state[key].loading = true;
    }
  });

  builder.addCase(thunk.fulfilled, (state: any, action: any) => {
    state[key].data = action.payload.result;
    state[key].loading = false;
  });

  builder.addCase(thunk.rejected, (state: any) => {
    state[key].loading = false;
  });
};

const crmSettingSlice = createSlice({
  name: "crmSetting",
  initialState,
  reducers: {},
  extraReducers(builder) {
    createListReducers(builder, fetchCustomerStage, "customerStages");
    createListReducers(builder, fetchCustomerSource, "customerSource");
    createListReducers(builder, fetchVisitPurpose, "visitPurpose");
    createListReducers(builder, fetchCrmLeadType, "crmLead");
    createListReducers(builder, fetchCustomerAuditTeamMembers, "customerAuditTeamMembers");
    createListReducers(builder, fetchRoleCustomerViewLimits, "roleCustomerViewLimits");

    builder.addCase(deleteCustomerStage.fulfilled, (state, action) => {
      state.customerStages.data = state.customerStages.data.filter(
        (item: any) => item.Id !== action.payload.id,
      );
    });

    builder.addCase(deleteCustomerSource.fulfilled, (state, action) => {
      state.customerSource.data = state.customerSource.data.filter(
        (item: any) => item.Id !== action.payload.id,
      );
    });

    builder.addCase(deleteCrmLeadType.fulfilled, (state, action) => {
      state.crmLead.data = state.crmLead.data.filter(
        (item: any) => item.Id !== action.payload.id,
      );
    });

    builder.addCase(deleteVisitPurpose.fulfilled, (state, action) => {
      state.visitPurpose.data = state.visitPurpose.data.filter(
        (item: any) => item.Id !== action.payload.id,
      );
    });

    builder.addCase(fetchCrmSetting.fulfilled, (state, action) => {
      state.crmSetting = action.payload;
    });

    builder.addCase(fetchSaleTargetFields.fulfilled, (state, action) => {
      state.saleTargetFields = action.payload;
    });
  },
});

export default crmSettingSlice.reducer;
